import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Facebook, Instagram, Globe, MousePointerClick, Loader2 } from "lucide-react";

const RANGES = [{ label: "Last 7 days", days: 7 }, { label: "Last 30 days", days: 30 }, { label: "All time", days: null }];
const CHANNELS = [
  { key: "facebook", label: "Facebook", icon: Facebook, tone: "text-blue-600 bg-blue-50 dark:bg-blue-950/40" },
  { key: "instagram", label: "Instagram", icon: Instagram, tone: "text-pink-600 bg-pink-50 dark:bg-pink-950/40" },
  { key: "other", label: "Other sites", icon: Globe, tone: "text-amber-700 bg-amber-50 dark:bg-amber-950/40" },
  { key: "direct", label: "Direct / unknown", icon: MousePointerClick, tone: "text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40" },
];
const SITE = "https://mysprouts.org";
const LINKS = [
  ["Facebook post", "facebook", "post"], ["Facebook ad", "facebook", "ad"], ["Facebook story", "facebook", "story"],
  ["Instagram post / bio", "instagram", "post"], ["Instagram ad", "instagram", "ad"], ["Instagram story", "instagram", "story"],
].map(([label, src, med]) => ({ label, url: `${SITE}/register?utm_source=${src}&utm_medium=${med}&utm_campaign=launch` }));

const pct = (pro, n) => (n ? `${Math.round((pro / n) * 100)}%` : "–");

function ChannelCard({ c, stats }) {
  const Icon = c.icon;
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`source-card-${c.key}`}>
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${c.tone}`}><Icon className="h-5 w-5" /></div>
      <div className="mt-3 text-sm text-muted-foreground">{c.label}</div>
      <div className="text-3xl font-bold" data-testid={`source-signups-${c.key}`}>{stats.signups}</div>
      <div className="text-xs text-muted-foreground mt-1" data-testid={`source-pro-${c.key}`}>{stats.pro} became Pro · {pct(stats.pro, stats.signups)}</div>
    </div>
  );
}

function TrackingLinks() {
  const copy = (u) => navigator.clipboard.writeText(u).then(() => toast.success("Link copied"));
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="tracking-links">
      <h2 className="font-semibold">Tracking links</h2>
      <p className="text-sm text-muted-foreground mb-3">Use these in posts, ads and story link stickers so each sign-up is credited to the right place.</p>
      <div className="space-y-2">
        {LINKS.map((l, i) => (
          <div key={l.url} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
            <span className="sm:w-40 shrink-0 font-medium">{l.label}</span>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <code className="flex-1 min-w-0 break-all sm:truncate rounded-lg bg-secondary px-2 py-1 text-xs">{l.url}</code>
              <Button size="sm" variant="ghost" onClick={() => copy(l.url)} data-testid={`copy-link-${i}`}><Copy className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Breakdown({ rows }) {
  if (!rows.length) return <p className="text-sm text-muted-foreground" data-testid="breakdown-empty">No Facebook or Instagram sign-ups in this period yet.</p>;
  return (
    <table className="w-full min-w-[420px] text-sm" data-testid="breakdown-table">
      <thead><tr className="text-left text-muted-foreground"><th className="py-2 pr-3">Channel</th><th className="pr-3">Placement</th><th className="pr-3">Campaign</th><th className="text-right pr-3">Sign-ups</th><th className="text-right">Pro</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={`${r.channel}-${r.medium}-${r.campaign}`} className="border-t border-border">
            <td className="py-2 capitalize">{r.channel}</td><td className="capitalize">{r.medium}</td><td>{r.campaign}</td>
            <td className="text-right font-semibold">{r.signups}</td><td className="text-right">{r.pro}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function SignupSources() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    setData(null); setError(false);
    api.get("/admin/signup-sources", { params: days ? { days } : {} }).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [days, isAdmin, reload]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  const social = data ? data.channels.facebook.signups + data.channels.instagram.signups : 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="signup-sources-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">Sign-up sources</h1>
            <p className="text-sm text-muted-foreground" data-testid="sources-summary">
              {data ? `${data.total_signups} new sign-ups · ${social} from Facebook & Instagram` : error ? "Couldn't load the numbers." : "Loading…"}
            </p>
          </div>
          <div className="flex gap-1 rounded-xl bg-secondary p-1">
            {RANGES.map((r) => (
              <button key={r.label} onClick={() => setDays(r.days)} data-testid={`range-${r.days ?? "all"}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${days === r.days ? "bg-background shadow-sm" : "text-muted-foreground"}`}>{r.label}</button>
            ))}
          </div>
        </div>
        {error ? (
          <Button variant="secondary" onClick={() => setReload((n) => n + 1)} data-testid="sources-retry">Try again</Button>
        ) : !data ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {CHANNELS.map((c) => <ChannelCard key={c.key} c={c} stats={data.channels[c.key] || { signups: 0, pro: 0 }} />)}
            </div>
            <div className="rounded-2xl border border-border bg-card p-5 overflow-x-auto">
              <h2 className="font-semibold mb-2">Facebook & Instagram by placement</h2>
              <Breakdown rows={data.breakdown} />
            </div>
          </>
        )}
        <TrackingLinks />
      </div>
    </Layout>
  );
}
